import { SubmitHandler } from "react-hook-form";

import { useGameSummaryContext } from "../contexts/GameSummary";
import {
    IGameSummaryData,
    UmpireLocation,
    UmpireRatings,
} from "../types/gamesummary";

import { alertActions } from "../../../../../features/alert/alertSlice";
import { useAppDispatch } from "../../../../../hooks/reduxHooks";
import { useAuthenticatedUser } from "../../../../../hooks/useAuthenticatedUser";
import { updateGameSummary, updateUmpireRatings } from "../../../../../lib/api";

const useGameSummaryHandlers = () => {
    const { setFetchStatus, setSubmitButtonText, methods, game } =
        useGameSummaryContext();
    const user = useAuthenticatedUser();
    const dispatch = useAppDispatch();

    const handleStarHover = (
        setHoveredStars: React.Dispatch<React.SetStateAction<UmpireRatings>>,
        ratingType: keyof UmpireRatings,
        idx: number,
    ) => {
        setHoveredStars(prev => ({
            ...prev,
            [ratingType]: idx,
        }));
    };

    const handleStarLeave = (
        setHoveredStars: React.Dispatch<React.SetStateAction<UmpireRatings>>,
        ratingType: keyof UmpireRatings,
        tempRating: UmpireRatings | undefined,
    ) => {
        setHoveredStars(prev => ({
            ...prev,
            [ratingType]: tempRating?.[ratingType] || 0,
        }));
    };

    const handleStarClick = (
        location: UmpireLocation,
        rating: keyof UmpireRatings,
        idx: number,
        setTempRating: React.Dispatch<
            React.SetStateAction<UmpireRatings | undefined>
        >,
    ) => {
        setTempRating(prev => ({
            ...prev,
            [rating]: idx,
        }));
        methods.setValue(`${location}.${rating}`, idx, { shouldDirty: true });
    };

    const onSubmit: SubmitHandler<Partial<IGameSummaryData>> = async data => {
        setFetchStatus("pending");
        setSubmitButtonText("Submitting Game Data...");

        const umpirePromise = game.umpires
            ? updateUmpireRatings(
                  data,
                  game.id,
                  user!.id,
                  game.umpires!.reduce((acc: any, u) => {
                      acc[u.location] = u.id;
                      return acc;
                  }, {}),
              )
            : Promise.resolve();

        const dataSubmission = Promise.all([
            umpirePromise,
            updateGameSummary(data, game.id, user!.id),
        ]);

        const update1 = Math.floor(Math.random() * 500) + 750;
        const update2 = Math.floor(Math.random() * 500) + 1500;
        const timeout1 = setTimeout(() => {
            setSubmitButtonText("Updating Standings...");
        }, update1);

        const timeout2 = setTimeout(
            () => {
                setSubmitButtonText("Updating Playoff Brackets...");
            },
            update1 + (Math.floor(Math.random() * 500) + 2000),
        );

        const timeout3 = setTimeout(
            () => {
                setSubmitButtonText("Please wait...");
            },
            update1 + update2 + (Math.floor(Math.random() * 500) + 2000),
        );

        dataSubmission
            .then(() => {
                clearTimeout(timeout1);
                clearTimeout(timeout2);
                clearTimeout(timeout3);
                dispatch(
                    alertActions.success({
                        title: "Game summary updated successfully",
                        dismissible: true,
                    }),
                );

                methods.reset(undefined, { keepValues: true });
            })
            .catch(e => {
                clearTimeout(timeout1);
                clearTimeout(timeout2);
                clearTimeout(timeout3);
                dispatch(
                    alertActions.error({
                        title: "The server encountered a problem updating the game summary.",
                        text: "If the problem continues, please reach out to an admin.",
                        dismissible: true,
                    }),
                );
            })
            .finally(() => {
                setSubmitButtonText("Update Game Summary");
                setFetchStatus(undefined);
            });
    };

    return { handleStarClick, handleStarHover, handleStarLeave, onSubmit };
};

export default useGameSummaryHandlers;
